/**
 * One decode worker, one frame at a time, for both callers: the door's two lanes
 * and an upload's walk. A worker runs a frame to its end whatever the caller does,
 * so a frame posted behind an unanswered one only queues there and every frame
 * behind it answers late. The lane stays taken until the answer, and past the
 * stall limit it replaces the worker rather than post behind it.
 *
 * Load-failed and threw-on-one-frame both fire `error`; only the worker's own
 * ready message tells them apart. A worker file gone after a deploy never loads,
 * while a worker that threw once is still in service.
 */
import {
	BehaviorSubject,
	filter,
	finalize,
	fromEvent,
	map,
	merge,
	type Observable,
	of,
	Subject,
	shareReplay,
	take,
	takeUntil,
	timeout,
} from "rxjs";
import type {
	DecodeMessage,
	DecodeRequest,
	DecodeResponse,
	QrPolarity,
} from "./worker.ts";
import DecodeWorker from "./worker.ts?worker";

export type Frame = Pick<DecodeRequest, "width" | "height" | "buffer">;

export type LaneAnswer = {
	readonly token: string | null;
	// The frame's pixels handed back; null when the worker returned nothing.
	readonly frame: Frame | null;
	readonly tookMs: number;
	readonly stalled: boolean;
};

// One replaced worker can be a slow network. Two that never loaded is a door
// that will not read.
const UNSTARTED_STALLS_BEFORE_FAILING = 2;

export function createDecodeLane(
	stalledMs: number,
	stopped$: Observable<void>,
) {
	const failed$ = new BehaviorSubject(false);
	const reset$ = new Subject<void>();
	let worker: Worker;
	let frameId = 0;
	let frameOut = false;
	let started = false;
	let unstartedStalls = 0;

	const spawn = () => {
		worker = new DecodeWorker();
		started = false;
		merge(
			fromEvent(worker, "message").pipe(map(() => true)),
			fromEvent(worker, "error").pipe(map(() => false)),
		)
			.pipe(take(1), takeUntil(merge(stopped$, reset$)))
			.subscribe((loaded) => {
				if (loaded) {
					started = true;
					unstartedStalls = 0;
				} else {
					failed$.next(true);
				}
			});
	};
	spawn();

	return {
		failed$,
		get free() {
			return !frameOut && !failed$.value;
		},
		read(frame: Frame, polarity: QrPolarity): Observable<LaneAnswer> {
			const id = ++frameId;
			const postedAt = performance.now();
			const answer$ = merge(
				fromEvent<MessageEvent<DecodeMessage>>(worker, "message").pipe(
					map((ev) => ev.data),
					filter(
						(data): data is DecodeResponse => "id" in data && data.id === id,
					),
					map(({ token, buffer }) => ({
						token,
						frame: { width: frame.width, height: frame.height, buffer },
						stalled: false,
					})),
				),
				merge(
					fromEvent(worker, "error"),
					fromEvent(worker, "messageerror"),
				).pipe(map(() => ({ token: null, frame: null, stalled: false }))),
			).pipe(
				take(1),
				timeout({
					first: stalledMs,
					with: () => {
						worker.terminate();
						if (!started) unstartedStalls++;
						if (unstartedStalls >= UNSTARTED_STALLS_BEFORE_FAILING) {
							failed$.next(true);
						} else {
							spawn();
						}
						return of({ token: null, frame: null, stalled: true });
					},
				}),
				map((answer) => ({
					...answer,
					tookMs: performance.now() - postedAt,
				})),
				takeUntil(merge(stopped$, reset$)),
				finalize(() => {
					frameOut = false;
				}),
				shareReplay({ bufferSize: 1, refCount: false }),
			);
			frameOut = true;
			worker.postMessage({ id, ...frame, polarity } satisfies DecodeRequest, [
				frame.buffer,
			]);
			return answer$;
		},
		/** A frame out or a failed start gets a fresh worker; a free lane keeps its own. */
		reset() {
			const stuck = frameOut || failed$.value;
			reset$.next();
			frameOut = false;
			unstartedStalls = 0;
			if (stuck) {
				worker.terminate();
				spawn();
			}
			failed$.next(false);
		},
		terminate: () => worker.terminate(),
	};
}
